import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  output,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { CommonUtilityService } from '@app/services/common-utility.service';
import { LinkSpacebarActivationDirective } from '@app/shared/directives/link-spacebar-activation/link-spacebar-activation.directive';

interface AppRoutesToken {
  readonly SIGNUP: string;
}

@Component({
  selector: 'app-verify-your-identity-limit-reached',
  templateUrl: './verify-your-identity-limit-reached.component.html',
  styleUrl: './verify-your-identity-limit-reached.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [LinkSpacebarActivationDirective],
})
export class VerifyYourIdentityLimitReachedComponent implements OnInit {
  private static readonly PHONE_LINK_PATTERN =
    /^(<a href="tel:\+?[\d-]+">)([\d+() -]+)(<\/a>)$/;
  private static readonly DEFAULT_HEADER = 'Verification Limit Reached';
  private static readonly DEFAULT_MESSAGE =
    "You've reached the maximum number of one-time code verification attempts. Please try again after 24 hours, or contact Guest Services at {0} for help.";
  private static readonly DEFAULT_PHONE_LINK =
    '<a href="tel:+18007756750">+1-800-775-6750</a>';

  readonly canceled = output<void>();
  private readonly appRoutes = inject<AppRoutesToken>('AppRoutes' as any);

  pageLabels: Record<string, string> = {};
  eventTargeturl: string | undefined;
  pageHeading: string = '';

  private readonly common = inject(CommonUtilityService);
  private readonly title = inject(Title);
  private readonly sanitizer = inject(DomSanitizer);
  readonly globalDefault = inject<any>('GlobalDefaults' as any);

  ngOnInit(): void {
    this.eventTargeturl = this.common.getTargetUrl(this.appRoutes.SIGNUP);
    this.pageLabels = this.common.getResolvedPageData() ?? {};
    this.pageHeading =
      this.pageLabels?.[
        'com.udx.account.pageinfo.verification.limit.reached'
      ] || this.globalDefault.VERIFICATION_RATE_LIMIT_REACHED;
    this.title.setTitle(this.limitReachedHeader);
    this.common.scrollTop();
    this.common.triggerDDLEventHandler(
      this.pageLabels?.['com.udx.account.pageinfo.verification.limit.reached'],
      {
        isPageLoad: true,
      },
    );
    this.common.setPageName(this.pageHeading);
  }

  get limitReachedHeader(): string {
    return (
      this.pageLabels?.['com.udx.account.onetimepasscode.limitreachedheader'] ||
      VerifyYourIdentityLimitReachedComponent.DEFAULT_HEADER
    );
  }

  get limitReachedBodyDescription(): string {
    const message =
      this.pageLabels?.[
        'com.udx.account.onetimepasscode.verification_limit_reached_message'
      ] || VerifyYourIdentityLimitReachedComponent.DEFAULT_MESSAGE;
    const resolvedPhoneLink =
      this.pageLabels?.['com.udx.account.onetimepasscode.guest_services_phone'];
    const phoneLink =
      resolvedPhoneLink &&
      VerifyYourIdentityLimitReachedComponent.PHONE_LINK_PATTERN.test(
        resolvedPhoneLink,
      )
        ? resolvedPhoneLink
        : VerifyYourIdentityLimitReachedComponent.DEFAULT_PHONE_LINK;

    const unbreakablePhoneLink =
      VerifyYourIdentityLimitReachedComponent.withUnbreakablePhoneText(
        phoneLink,
      );
    const description = message.includes('{0}')
      ? message.replace('{0}', unbreakablePhoneLink)
      : `${message} ${unbreakablePhoneLink}`;

    return (
      this.sanitizer.sanitize(SecurityContext.HTML, description) ??
      VerifyYourIdentityLimitReachedComponent.DEFAULT_MESSAGE.replace(
        '{0}',
        VerifyYourIdentityLimitReachedComponent.DEFAULT_PHONE_LINK,
      )
    );
  }

  // Swaps breakable hyphens/spaces for non-breaking equivalents so the number can't wrap mid-digit.
  private static withUnbreakablePhoneText(phoneLinkHtml: string): string {
    const match =
      VerifyYourIdentityLimitReachedComponent.PHONE_LINK_PATTERN.exec(
        phoneLinkHtml,
      );
    if (!match) {
      return phoneLinkHtml;
    }
    const [, openTag, text, closeTag] = match;
    const unbreakableText = text
      .replaceAll('-', '\u2011')
      .replaceAll(' ', '\u00A0');
    return `${openTag}${unbreakableText}${closeTag}`;
  }

  onCancel(): void {
    this.canceled.emit();
  }
  onSpacebarPress(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.preventDefault();
      this.onCancel();
    }
  }
}
